/**
 * src/components/spatial/MapEntityPopover.tsx
 *
 * Lightweight floating detail card for a picked aggregate map entity
 * (country policy, execution rule, agent, presence cell, drone). Mirrors
 * the spatial-object detail panel's placement/anchor behaviour but stays
 * read-only — aggregate features carry no per-object actions.
 *
 * Reads the current selection from MapDataContext; renders nothing for
 * spatial-object selections (those own the richer interaction panel).
 */

import React from 'react';
import { useMapDataOptional, type MapEntityType } from './MapDataContext';
import type { MapFeature } from '../../services/spatial/map/mapTypes';

const ENTITY_LABEL: Record<MapEntityType, string> = {
  spatialObject: 'Spatial object',
  country: 'Country policy',
  executionRule: 'Execution rule',
  ruleEvent: 'Rule event',
  agent: 'Agent',
  liveSession: 'Live session',
  presenceCell: 'Presence cell',
  drone: 'Drone',
  transaction: 'Transaction',
};

/** Human title for a picked feature (name-ish property → id → label). */
function titleFor(entityType: MapEntityType, feature: MapFeature | null | undefined): string {
  const p = (feature?.properties ?? {}) as Record<string, unknown>;
  const named =
    p.name ?? p.title ?? p.label ?? p.countryName ?? p.agentName ?? p.ruleName ?? p.code ?? feature?.id;
  return named != null && String(named).trim() ? String(named) : ENTITY_LABEL[entityType];
}

/** Format a scalar for display; returns null for non-scalars / empties. */
function formatScalar(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value === 'string') return value.trim() ? value : null;
  return null;
}

/** Turn a camelCase / snake_case key into a readable label. */
function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^\w/, (c) => c.toUpperCase());
}

// Keys that are noise in a detail card (geometry helpers, internal ids we
// already show in the title, style hints).
const SKIP_KEYS = new Set([
  'name', 'title', 'label', 'id', 'objectId', 'geometry', 'bbox',
  'color', 'colour', 'fill', 'stroke', 'icon',
]);

const MapEntityPopover: React.FC = () => {
  const mapData = useMapDataOptional();
  const sel = mapData?.state.selectedEntity;

  if (!mapData || !sel || sel.entityType === 'spatialObject' || !sel.entityFeature) return null;

  const feature = sel.entityFeature;
  const props = (feature.properties ?? {}) as Record<string, unknown>;

  const rows: Array<[string, string]> = [];
  for (const [key, raw] of Object.entries(props)) {
    if (SKIP_KEYS.has(key)) continue;
    const formatted = formatScalar(raw);
    if (formatted != null) rows.push([humanizeKey(key), formatted]);
    if (rows.length >= 8) break;
  }

  const anchor = sel.anchor;
  const style: React.CSSProperties = {
    position: 'absolute',
    zIndex: 6,
    // Anchor near the click when we have pixel coords, else top-left.
    left: anchor ? Math.max(8, anchor.x + 12) : 12,
    top: anchor ? Math.max(8, anchor.y + 12) : 12,
    maxWidth: 280,
    background: 'rgba(20,22,28,0.95)',
    color: '#e8eaf0',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.1)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
    fontSize: 12,
    overflow: 'hidden',
  };

  return (
    <div style={style} role="dialog" aria-label={`${ENTITY_LABEL[sel.entityType]} details`}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, color: '#8890a4' }}>
            {ENTITY_LABEL[sel.entityType]}
          </div>
          <div
            style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={titleFor(sel.entityType, feature)}
          >
            {titleFor(sel.entityType, feature)}
          </div>
        </div>
        <button
          onClick={() => mapData.selectEntity(null)}
          title="Close"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#aab2c5', fontSize: 14, padding: 2 }}
        >✕</button>
      </div>

      <div style={{ padding: '8px 10px', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px' }}>
        {rows.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', color: '#8890a4' }}>No additional detail.</div>
        ) : (
          rows.map(([k, v]) => (
            <React.Fragment key={k}>
              <span style={{ color: '#8890a4', whiteSpace: 'nowrap' }}>{k}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v}>{v}</span>
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
};

export default MapEntityPopover;
